import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Camera, ChevronRight, AlertTriangle, CheckCircle2, Image as ImageIcon } from "lucide-react";

/**
 * Photo Coverage Report — at-a-glance view of which products still need
 * per-size photography. A product's coverage = (variants with ≥1 own photo)
 * ÷ (sellable variants), expressed as 0–100%. Rows sort by lowest coverage
 * first so the work queue is at the top.
 */
export default function AdminPhotoCoverage() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/products", { params: { active_only: false } })
            .then(r => setProducts(r.data))
            .finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        const lc = q.trim().toLowerCase();
        const filtered = lc
            ? products.filter((p) => p.name.toLowerCase().includes(lc) || (p.slug || "").includes(lc))
            : products;
        const computed = filtered.map((p) => {
            const sellable = (p.variants || []).filter((v) => Number(v.price) > 0);
            const withOwn = sellable.filter((v) => (v.images || []).length > 0);
            const total = sellable.length;
            const coverage = total === 0 ? 0 : Math.round((withOwn.length / total) * 100);
            const sharedPhotoCount = (p.images || []).length;
            return {
                p,
                total,
                covered: withOwn.length,
                missing: total - withOwn.length,
                coverage,
                sharedPhotoCount,
                hasAnyPhotos: sharedPhotoCount > 0 || withOwn.length > 0,
            };
        });
        // Sort by lowest coverage first (so "needs work" floats to the top),
        // then by missing-count desc, then by product name.
        computed.sort((a, b) => {
            if (a.coverage !== b.coverage) return a.coverage - b.coverage;
            if (a.missing !== b.missing) return b.missing - a.missing;
            return a.p.name.localeCompare(b.p.name);
        });
        return computed;
    }, [products, q]);

    const summary = useMemo(() => {
        const total = products.length;
        const fully = rows.filter((r) => r.coverage === 100 && r.total > 0).length;
        const partial = rows.filter((r) => r.coverage > 0 && r.coverage < 100).length;
        const zero = rows.filter((r) => r.coverage === 0 && r.total > 0).length;
        const noSizes = rows.filter((r) => r.total === 0).length;
        const avg = rows.length === 0 ? 0 : Math.round(rows.reduce((s, r) => s + r.coverage, 0) / rows.length);
        return { total, fully, partial, zero, noSizes, avg };
    }, [products, rows]);

    return (
        <div data-testid="admin-photo-coverage" className="space-y-6">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-1.5"><Camera className="w-3 h-3" />Catalog quality</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Photo coverage</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Coverage = (variants with their own photos) ÷ (sellable variants). Products with the lowest coverage float to the top of the list so you can plan your next photo shoot. Shared product photos are tracked separately.
                    </p>
                </div>
                <div className="text-right">
                    <div className="font-serif text-4xl text-stone-900" data-testid="coverage-avg">{summary.avg}%</div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Average catalog coverage</div>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="coverage-kpis">
                <Kpi label="Fully covered" value={summary.fully} hint="All sizes have ≥1 photo" tone="emerald" testId="kpi-fully" />
                <Kpi label="Partial coverage" value={summary.partial} hint="Some sizes still need shots" tone="amber" testId="kpi-partial" />
                <Kpi label="Zero size photos" value={summary.zero} hint="Relying on shared gallery only" tone="rose" testId="kpi-zero" />
                <Kpi label="No sellable sizes" value={summary.noSizes} hint="Add a size with price > 0" tone="stone" testId="kpi-no-sizes" />
            </div>

            <div className="bg-white border border-stone-200 p-3">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter by product name or slug…"
                    className="rounded-none border-stone-300"
                    data-testid="coverage-search"
                />
            </div>

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Product</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Coverage</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Sizes</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Shared photos</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={5} className="p-8 text-center text-stone-500">
                                {q ? "No products match your search." : "No products in catalog yet."}
                            </td></tr>
                        ) : rows.map(({ p, total, covered, missing, coverage, sharedPhotoCount, hasAnyPhotos }) => (
                            <tr key={p.id} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50/60" data-testid={`coverage-row-${p.slug}`}>
                                <td className="p-3 align-top">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-stone-100 shrink-0 border border-stone-200 overflow-hidden">
                                            {p.images?.[0]
                                                ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                                : <div className="w-full h-full grid place-items-center text-stone-400"><ImageIcon className="w-4 h-4" /></div>}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium text-stone-900 truncate">{p.name}</div>
                                            <div className="text-[11px] text-stone-500 truncate">{p.slug}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-3 align-middle min-w-[220px]">
                                    <CoverageBar coverage={coverage} total={total} hasAnyPhotos={hasAnyPhotos} />
                                </td>
                                <td className="p-3 align-middle text-right">
                                    {total === 0 ? (
                                        <span className="text-[11px] text-stone-400 italic">no sizes</span>
                                    ) : (
                                        <span className="text-sm font-mono">
                                            <span className={covered === total ? "text-emerald-700" : "text-stone-700"}>{covered}</span>
                                            <span className="text-stone-400"> / {total}</span>
                                        </span>
                                    )}
                                </td>
                                <td className="p-3 align-middle text-right text-sm font-mono text-stone-600">
                                    {sharedPhotoCount}
                                </td>
                                <td className="p-3 align-middle text-right">
                                    <Link
                                        to={`/admin/products?focus=${p.id}`}
                                        className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 text-xs font-medium"
                                        data-testid={`coverage-edit-${p.slug}`}
                                        title={missing > 0 ? `Add photos for ${missing} more size${missing > 1 ? "s" : ""}` : "Open product editor"}
                                    >
                                        {missing > 0 ? `Add ${missing} photo${missing > 1 ? "s" : ""}` : "Edit"} <ChevronRight className="w-3.5 h-3.5" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-[11px] text-stone-400 italic">
                Tip: products with full coverage get the small dot badge on the shop grid and unlock the "Has custom size photos" filter.
            </p>
        </div>
    );
}

const Kpi = ({ label, value, hint, tone, testId }) => {
    const palette = {
        emerald: { bar: "bg-emerald-100", text: "text-emerald-800", icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> },
        amber:   { bar: "bg-amber-100",   text: "text-amber-800",   icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-700" /> },
        rose:    { bar: "bg-rose-100",    text: "text-rose-800",    icon: <AlertTriangle className="w-3.5 h-3.5 text-rose-700" /> },
        stone:   { bar: "bg-stone-100",   text: "text-stone-700",   icon: <ImageIcon className="w-3.5 h-3.5 text-stone-500" /> },
    }[tone] || { bar: "bg-stone-100", text: "text-stone-700", icon: null };
    return (
        <div className={`border border-stone-200 ${palette.bar} p-3`} data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-600">{palette.icon}<span>{label}</span></div>
            <div className={`font-serif text-3xl mt-1 ${palette.text}`}>{value}</div>
            <div className="text-[11px] text-stone-500 mt-1">{hint}</div>
        </div>
    );
};

const CoverageBar = ({ coverage, total, hasAnyPhotos }) => {
    if (total === 0) {
        return (
            <div className="text-[11px] text-stone-400 italic">No sellable sizes</div>
        );
    }
    const tone =
        coverage === 100 ? "bg-emerald-600" :
            coverage >= 50 ? "bg-amber-500" :
                coverage > 0 ? "bg-orange-500" :
                    "bg-rose-500";
    return (
        <div>
            <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-mono">{coverage}%</span>
                {coverage === 0 && hasAnyPhotos && (
                    <span className="text-[10px] text-stone-500 uppercase tracking-widest">uses shared gallery only</span>
                )}
            </div>
            <div className="h-1.5 bg-stone-200 overflow-hidden">
                <div className={`h-full ${tone} transition-all`} style={{ width: `${coverage}%` }} />
            </div>
        </div>
    );
};
